import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { useStyles } from './Common.styled';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Grid, Paper } from '@material-ui/core';
import { Card, Table } from 'components/UI';
import { ReservationsActions } from 'state/modules/reservations';
import moment from 'moment';

const ReservationsTable = ({ accountStatementId, type }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 5,
    account_statement_id: accountStatementId,
  });

  const DatetimeField = ({ datetime, tz = moment.tz.guess() }) => {
    return (
      <span>
        <p>{moment(datetime).tz(tz).format(`${localeFormat} hh:mm A`)}</p>
        <p className={classes.timezone}>{moment().tz(tz).format('UTC Z')}</p>
      </span>
    );
  };

  const loadingReservations = useSelector((state) => state.reservations?.loading);
  const data = useSelector((state) => state.reservations?.reservations);
  const pagination = useSelector((state) => state.reservations?.pagination);
  const localeFormat = useSelector((state) => state.organizations?.localeFormat);

  const loading = loadingReservations;

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '6rem',
      sortable: false,
    },
    {
      name: i18n.t('common.start'),
      selector: 'total_emitted',
      cell: (row) =>
        row?.start_reserved ? (
          <div>
            <DatetimeField datetime={row?.start_reserved} tz={row?.vehicle_group?.time_zone} />
            {row?.start_used && (
              <div className={classes?.time}>
                <DatetimeField datetime={row?.start_used} tz={row?.vehicle_group?.time_zone} />
              </div>
            )}
          </div>
        ) : (
          '--'
        ),
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.end'),
      selector: 'total_emitted',
      cell: (row) =>
        row?.end_reserved ? (
          <div>
            <DatetimeField datetime={row?.end_reserved} tz={row?.vehicle_group?.time_zone} />
            {row?.end_used && (
              <div className={classes?.time}>
                <DatetimeField datetime={row?.end_used} tz={row?.vehicle_group?.time_zone} />
              </div>
            )}
          </div>
        ) : (
          '--'
        ),
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.vehicle'),
      cell: (row) => row?.vehicle?.name || '--',
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.location'),
      selector: 'total_emitted',
      cell: (row) => row?.start_reserved_location?.name || '--',
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.fleet'),
      cell: (row) => row?.vehicle_group?.parent?.name || '--',
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.status'),
      selector: 'total_emitted',
      cell: (row) =>
        row?.status ? (
          <div className={`${classes.status} ${classes[row?.status.toLowerCase()]}`}>
            <span className="status">
              {i18n.t(`reservations_statuses.${row?.status}`, {
                defaultValue: row?.status,
              })}
            </span>
          </div>
        ) : (
          '--'
        ),
      grow: 1,
      sortable: false,
    },
  ];

  /**
   * Get comments
   * @param {object} params
   */
  const fetchReservations = (params) =>
    dispatch(ReservationsActions.fetchReservations({ ...params }));

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handlePageChange = (newPage) => {
    const newParams = { ...params, page: newPage };
    setParams(newParams);
    fetchReservations(newParams);
  };

  /**
   * Handle limit / page size change
   * @param {object} event Value of the input
   */
  const handleChangeRowsPerPage = (event) => {
    const newParams = { ...params, limit: event.target.value };
    setParams(newParams);
    fetchReservations(newParams);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];

  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="invoice-title">
          {i18n.t('account_statement.associated_reservations', {
            reservations: pagination?.totalRows || '-',
          })}
        </Box>
      </Typography>
    </Box>
  );

  useEffect(() => {
    fetchReservations(params);
  }, []); // eslint-disable-line

  return (
    <Grid item xs={12}>
      <Card title={title} menuItems={items} headerActionType="vertical">
        <Box className={classes.container}>
          <Paper elevation={2} className={classes.paperInner}>
            <Table
              data={data}
              columns={columns}
              loading={loading}
              page={params?.page}
              limit={params?.limit}
              handlePageChange={handlePageChange}
              totalRows={pagination?.totalRows || 0}
              handlePerRowsChange={null}
              showFirstButton={false}
              showLastButton={false}
              rowsPerPageOptions={[5]}
            />
          </Paper>
        </Box>
      </Card>
    </Grid>
  );
};

export { ReservationsTable };
